import { Challenges } from '../constants'
import { deserializeDestroyer, deserializeSolver, deserializeUpgrade } from '../deserializers'
import { getSudoku } from 'fake-sudoku-puzzle-generator'
import { getBoxIndicies, inverse, isComplete, getSpot } from '../utils'
import Destroyer from '../classes/destroyer'
import Upgrade from '../classes/upgrade'
import Solver from '../classes/solver'
import RowSolver from '../classes/row_solver'
import BoxSolver from '../classes/box_solver'
import ColSolver from '../classes/col_solver'
import CreditsPerPuzzleUpgrade from '../classes/credits_per_puzzle_upgrade'
import CreditsPerCellUpgrade from '../classes/credits_per_cell_upgrade'
import CreditsPerPossibilityUpgrade from '../classes/credits_per_possibility_upgrade'
import GuessData from '../classes/guess_data'

// TODO: put behind isdev, along with all other dev starting stuff
const STARTING_CREDITS = 0

function isValid (vals: number[][]): boolean {
  const decided = vals.filter((possible) => possible.length === 1).map((possible) => possible[0])
  return decided.length === (new Set(decided)).size
}

function getNewPuzzle (): number[][][] {
  // seconds for 10 puzzles w/ all 3 solvers unlocked at 1 sec each
  // VeryEasy = 986.982
  // Easy = 961.832
  // Medium = 1043.214
  // Hard = 1081.168
  const puzzle = getSudoku('VeryEasy')
  return puzzle.map((row) => row.map((spot) => {
    if (spot) {
      return [spot]
    }
    return [1, 2, 3, 4, 5, 6, 7, 8, 9]
  }))
}

export default class Game {
  // TODO: make these all private? (or some, think about it idk)
  public credits: Record<string, number>;
  public puzzle: number[][][];
  public noAutos: number[];
  public locked: boolean[][];
  public upgrades: Upgrade[];
  public stats: Record<string, number>;
  private rotateInterval: number;
  private solvers: Solver[];
  private noClicks: number[][];
  private destroyers: Destroyer[];
  private permanant: Record<string, number>;
  private currentTotalPossibilities: number;
  private currentSolvedCells: number;
  public challengeStats: Record<string, number>[];
  public guessData: GuessData;
  constructor (save: Record<string, unknown>) {
    this.puzzle = (save.puzzle as number[][][]) || getNewPuzzle()

    this.guessData = new GuessData(this.puzzle, save.guessData as Record<string, unknown>)

    this.noAutos = (save.noAutos as number[]) || []

    this.rotateInterval = (save.rotateInterval as number) || 0

    this.noClicks = (save.noClicks as number[][]) || []

    this.locked = (save.locked as boolean[][]) || []
    // if no saved, lock all things picked (assume its a new puzzle)
    if (this.locked.length === 0) {
      this.locked.push(...this.puzzle.map((row) => row.map((possibilities) => possibilities.length === 1)))
    }

    this.credits = (save.credits as Record<string, number>) || {
      current: STARTING_CREDITS,
      perPuzzle: 25,
      perPossibility: 0,
      perCell: 5
    }

    this.stats = (save.stats as Record<string, number>) || {
      possibilitiesEliminated: 0,
      cellsSolved: 0,
      puzzlesSolved: 0,
      puzzlesFailed: 0,
      totalCredits: 0
    }

    this.permanant = (save.permanant as Record<string, number>) || {
      creditMultiplier: 1
    }

    this.upgrades = save.upgrades ? (save.upgrades as Record<string, string>[]).map((upgrade) => deserializeUpgrade(upgrade, this.credits)) : [
      new CreditsPerPossibilityUpgrade(this.credits),
      new CreditsPerCellUpgrade(this.credits),
      new CreditsPerPuzzleUpgrade(this.credits)
    ]

    this.solvers = save.solvers ? (save.solvers as Record<string, number>[]).map((solver) => deserializeSolver(solver, this.puzzle, this.credits, this.noAutos)) : [
      new RowSolver(this.puzzle, this.credits, this.noAutos),
      new ColSolver(this.puzzle, this.credits, this.noAutos),
      new BoxSolver(this.puzzle, this.credits, this.noAutos)
    ]

    this.destroyers = save.destroyers ? (save.destroyers as Record<string, number>[]).map((destroyer) => deserializeDestroyer(destroyer, this.puzzle, this.locked)) : []

    this.challengeStats = (save.challengeStats as Record<string, number>[]) || Challenges.map(() => {
      return {
        isActive: false,
        solvedBefore: Infinity,
        challengeNumber: 0
      }
    })

    this.currentTotalPossibilities = this.puzzle.flat().flat().length
    this.currentSolvedCells = this.puzzle.map((row) => row.filter((cell) => cell.length === 1)).flat().length
  }

  isPuzzleComplete (): boolean {
    return isComplete(this.puzzle)
  }

  rotatePuzzle () {
    const newPuzzle = this.puzzle[0].map((_, colIndex) => this.puzzle.map((row) => row[colIndex]).reverse())
    this.puzzle.splice(0)
    this.puzzle.push(...newPuzzle)
    const newLocks = this.locked[0].map((_, colIndex) => this.locked.map((row) => row[colIndex]).reverse())
    this.locked.splice(0)
    this.locked.push(...newLocks)
  }

  resetPuzzle () {
    this.puzzle.splice(0)
    this.puzzle.push(...getNewPuzzle())
    this.locked.splice(0)
    this.locked.push(...this.puzzle.map((row) => row.map((possibilities) => possibilities.length === 1)))
    this.currentTotalPossibilities = this.puzzle.flat().flat().length
    this.currentSolvedCells = this.puzzle.map((row) => row.filter((cell) => cell.length === 1)).flat().length

    this.setNoClicks(this.noClicks.length)
    this.setNoAutos(this.noAutos.length)
  }

  isPuzzleValid () {
    return this.rowsAreValid() && this.colsAreValid() && this.boxesAreValid()
  }

  rowsAreValid () {
    return this.puzzle.every((row) => isValid(row))
  }

  colsAreValid () {
    return inverse(this.puzzle).every((row) => isValid(row))
  }

  getBox (boxNum: number) {
    const indicies = getBoxIndicies(boxNum)
    return indicies.map((index) => this.puzzle[index[0]][index[1]])
  }

  boxesAreValid () {
    const boxes = []
    for (let i = 0; i < 9; i++) {
      boxes.push(this.getBox(i))
    }
    return boxes.every((box) => isValid(box))
  }

  clearChallenge () {
    this.setNoAutos(0)
    this.setDestroyers(0)
    this.setNoClicks(0)
    clearInterval(this.rotateInterval)
  }

  setNoAutos (amt: number) {
    this.noAutos.splice(0)
    const noAutos = new Set<number>()
    while (noAutos.size < amt) {
      noAutos.add(Math.floor(Math.random() * 27))
    }
    this.noAutos.push(...noAutos)
  }

  resetGame ({
    destroyers = 0,
    noClicks = 0,
    noAutos = 0,
    rotate = 0
  }: {
    destroyers: number;
    noClicks: number;
    noAutos: number;
    rotate: number;
  }) {
    this.resetPuzzle()
    this.credits.current = STARTING_CREDITS
    this.credits.perPuzzle = 25
    this.credits.perPossibility = 0
    this.credits.perCell = 5
    this.setNoAutos(noAutos)
    this.setDestroyers(destroyers)
    this.solvers.forEach((solver) => solver.reset())
    this.upgrades.forEach((upgrade) => upgrade.reset())
    this.setNoClicks(noClicks)
    if (this.rotateInterval) {
      clearInterval(this.rotateInterval)
    }
    if (rotate) {
      this.rotateInterval = setInterval(() => this.rotatePuzzle(), rotate)
    }
  }

  setNoClicks (amt: number) {
    this.noClicks.splice(0)
    const noClicks = new Set<number>()
    while (noClicks.size < amt) {
      const spot = Math.floor(Math.random() * 81)
      const [x, y] = getSpot(spot)
      if (!this.locked[y][x]) {
        noClicks.add(spot)
      }
    }
    noClicks.forEach((noClick) => this.noClicks.push(getSpot(noClick)))
  }

  setDestroyers (amt: number) {
    this.destroyers.forEach((destroyer) => destroyer.remove())
    this.destroyers.splice(0)
    for (let i = 0; i < amt; i++) {
      this.destroyers.push(new Destroyer(this.puzzle, this.locked, 10000))
    }
  }

  onPuzzleChange (newPuzzle: number[][][]) {
    if (!this.isPuzzleValid()) {
      this.resetPuzzle()
      this.stats.puzzlesFailed += 1
      return
    }
    const newPossibilities = newPuzzle.flat().flat().length
    const possibilitiesEliminated = this.currentTotalPossibilities - newPuzzle.flat().flat().length
    let earnedCredits = Math.max(possibilitiesEliminated, 0) * this.credits.perPossibility
    this.stats.possibilitiesEliminated += possibilitiesEliminated
    const newSolvedCells = newPuzzle.map((row) => row.filter((cell) => cell.length === 1)).flat().length
    const solvedCells = newSolvedCells - this.currentSolvedCells
    earnedCredits += Math.max(solvedCells, 0) * this.credits.perCell
    this.stats.cellsSolved += solvedCells
    this.currentTotalPossibilities = newPossibilities
    this.currentSolvedCells = newSolvedCells
    if (this.isPuzzleComplete()) {
      this.resetPuzzle()
      earnedCredits += this.credits.perPuzzle
      this.stats.puzzlesSolved++
    }
    const totalCredits = (earnedCredits * this.permanant.creditMultiplier)
    this.stats.totalCredits += totalCredits
    this.credits.current += totalCredits
  }

  serialize (): Record<string, unknown> {
    return {
      puzzle: this.puzzle,
      noAutos: this.noAutos,
      rotateInterval: this.rotateInterval,
      noClicks: this.noClicks,
      locked: this.locked,
      stats: this.stats,
      credits: this.credits,
      permanant: this.permanant,
      upgrades: this.upgrades.map((upgrade) => upgrade.serialize()),
      solvers: this.solvers.map((solver) => solver.serialize()),
      destroyers: this.destroyers.map((destroyer) => destroyer.serialize()),
      challengeStats: this.challengeStats,
      guessData: this.guessData.serialize()
    }
  }
}
